import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ACCESS_TOKEN_KEY, CLIENT_AUTHORIZATION, REFRESH_TOKEN_KEY, TOKEN_EXPIRY_KEY } from './constants';
import JwtDecode from 'jwt-decode';
import { AppState } from '../store/store';
import { MfaRegistrationStatus } from '../api';
import dayjs from 'dayjs';
import queryClient from '../store/queryClient';

export interface StoredToken {
  accessToken: string;
  refreshToken: string;
  expiryTimestamp: number;
  expiresIn: number;
}

export const getStoredToken = (): StoredToken | null => {
  if (window.localStorage) {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const expiryTimestamp = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (accessToken && refreshToken && expiryTimestamp) {
      return {
        accessToken: accessToken,
        refreshToken: refreshToken,
        expiryTimestamp: Number(expiryTimestamp),
        expiresIn: Number(expiryTimestamp) - dayjs().unix(),
      };
    }
  }
  return null;
};

export async function revokeToken(token: string): Promise<Response> {
  const formData = new FormData();
  formData.append('token', token);

  const request = {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: CLIENT_AUTHORIZATION,
    },
  };

  const response = await fetch(`/oauth/revoke`, request);

  queryClient.removeQueries();

  return response;
}

export interface AuthState {
  tokenResponse: TokenResponse | null;
  mfaToken: string | null;
  mfaStatus: MfaRegistrationStatus | null;
  forgotPasswordSent: boolean;
}

export interface TokenResponse {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  scope?: string;
  tokenType?: string;
  error?: string;
  errorDescription?: string;
  mfaToken?: string;
}

export interface DecodedToken {
  authorities: string[];
  sub: string;
  exp: number;
  organisationId?: number;
  userId?: number;
}

const ADMIN_ROLE = 'ROLE_ADMIN';

// in future, make into a hook an ddeprecate all selectors below
export const getTokenInfo = (token: DecodedToken | null) => {
  if (!token) {
    return null;
  }
  return {
    ...token,
    isAdmin: token.authorities.some((it) => it === ADMIN_ROLE),
  };
};

export const decodeToken = (token: string): DecodedToken | null => {
  try {
    return JwtDecode(token);
  } catch (_) {
    return null;
  }
};

const getDecodedToken = createSelector(
  (s: AppState) => (s.auth.tokenResponse ? s.auth.tokenResponse.accessToken : null),
  (t: string | null): DecodedToken | null => {
    if (t === null) {
      return null;
    }
    return decodeToken(t);
  }
);

export const getUserName = createSelector([getDecodedToken], (decodedToken: DecodedToken | null): string | null => {
  if (decodedToken === null) {
    return null;
  }
  return decodedToken.sub || null;
});

export const getUserId = createSelector([getDecodedToken], (decodedToken: DecodedToken | null): number | null => {
  if (decodedToken === null) {
    return null;
  }
  return decodedToken.userId || null;
});

export const getCustomerId = createSelector([getDecodedToken], (decodedToken: DecodedToken | null): number | null => {
  if (decodedToken === null) {
    return null;
  }
  return decodedToken.organisationId || null;
});

const initialState: AuthState = {
  tokenResponse: getStoredToken(),
  mfaToken: null,
  mfaStatus: null,
  forgotPasswordSent: false,
};

const storeToken = (state: AuthState, action: TokenResponse) => {
  if (window.localStorage) {
    const accessTokenExp = dayjs().add(action.expiresIn, 'seconds');

    localStorage.setItem(ACCESS_TOKEN_KEY, action.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, action.refreshToken);
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(accessTokenExp.unix()));
  }
  state.tokenResponse = action;
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthTokenOnRefresh: (state, action: PayloadAction<TokenResponse>) => {
      storeToken(state, action.payload);
    },
    setAuthTokenOnLogin: (state, action: PayloadAction<TokenResponse>) => {
      storeToken(state, action.payload);
    },
    setMfaToken: (state, action) => {
      state.mfaToken = action.payload;
    },
    logoutAndDeleteToken: (state) => {
      if (window.localStorage) {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRY_KEY);
      }
      if (state.tokenResponse?.accessToken) {
        revokeToken(state.tokenResponse.accessToken).catch(() => null);
      }
      state.tokenResponse = null;
    },
    forgotPasswordSent: (state) => {
      state.forgotPasswordSent = true;
    },
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = authSlice;
// Extract and export each action creator by name
export const { setAuthTokenOnRefresh, setAuthTokenOnLogin, logoutAndDeleteToken, setMfaToken, forgotPasswordSent } =
  actions;
// Export the reducer, either as a default or named export
export default reducer;
